// 手机端使用单列输入与可换行工具栏，长地址完整阅读；电脑保留原布局，避免窄屏遮住表单或操作。
// 手机将明细按记录展示，保留原行事件；有交互表头或复杂行时保留可滑动表格，避免丢失排序、勾选和操作。
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "../components/ui/sonner";
import { Boxes, AlertTriangle, Save, PackagePlus, History } from "lucide-react";
import { fmtDT } from "../lib/date";

export default function MaterialInventory() {
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const lockedRegion = ["pm", "cdc"].includes(user?.role) && ["la", "norcal", "sd"].includes(user?.region) ? user.region : null;
  const [region, setRegion] = useState(lockedRegion || "la");
  const [data, setData] = useState({ rows: [], regions: [], lowCount: 0, canEdit: false });
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/inventory?region=${region}`);
      setData(data || { rows: [] });
      setEdits({});
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  }, [api, region]);

  useEffect(() => { load(); }, [load]);

  const setEdit = (key, field, val) => setEdits((s) => ({ ...s, [key]: { ...(s[key] || {}), [field]: val } }));
  const save = async (row) => {
    const e = edits[row.key] || {};
    const body = { region, key: row.key };
    if (e.balance !== undefined && e.balance !== "") body.balance = Number(e.balance);
    if (e.threshold !== undefined && e.threshold !== "") body.threshold = Number(e.threshold);
    if (body.balance === undefined && body.threshold === undefined) return;
    try { await api.post("/inventory/restock", body); toast.success(zh ? "库存已更新" : "Updated"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || err.message); }
  };

  const [showStockIn, setShowStockIn] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ key: "", qty: "", unitPrice: "", supplier: "", note: "" });
  const submitStockIn = async () => {
    if (!form.key || !(Number(form.qty) > 0)) { toast.error(zh ? "请选择材料并填写数量" : "Pick material & qty"); return; }
    try {
      await api.post("/inventory/stock-in", { region, key: form.key, qty: Number(form.qty), unitPrice: Number(form.unitPrice || 0), supplier: form.supplier, note: form.note });
      toast.success(zh ? "入库成功，库存已累加" : "Stocked in");
      setForm({ key: "", qty: "", unitPrice: "", supplier: "", note: "" });
      setShowStockIn(false);
      load();
      if (showHistory) loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get(`/inventory/stock-ins?region=${region}`); setHistory(data || []); }
    catch { /* ignore */ }
  }, [api, region]);
  useEffect(() => { if (showHistory) loadHistory(); }, [showHistory, loadHistory]);

  const regionName = (r) => (data.regions.find((x) => x.region === r) || {})[zh ? "name" : "nameEn"] || r;

  return (
    <div data-testid="material-inventory-page" className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Boxes className="h-6 w-6 text-brand-500" />
        <h1 className="text-2xl font-bold text-slate-900">{zh ? "材料库存管理" : "Material Inventory"}</h1>
        {data.lowCount > 0 && (
          <span data-testid="inv-low-count" className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded-full">
            <AlertTriangle className="h-3.5 w-3.5" />{zh ? `${data.lowCount} 项需补货` : `${data.lowCount} low`}
          </span>
        )}
      </div>
      <p className="text-slate-500 text-sm -mt-3">{zh ? "按分公司(LA/NorCal/SD)分别记录各材料余量；余量低于阈值标红提醒补货。确认采购时自动扣减对应分公司库存。" : "Per-branch material balances; low stock highlighted."}</p>

      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="inv-region-tabs" data-locked={lockedRegion || ""}>
        {(lockedRegion ? [{ r: lockedRegion }] : [{ r: "la" }, { r: "norcal" }, { r: "sd" }]).map(({ r }) => (
          <button key={r} data-testid={`inv-region-${r}`} onClick={() => setRegion(r)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${region === r ? "bg-white text-red-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            {regionName(r)}{lockedRegion && <span className="ml-1 text-[10px] text-slate-400 font-normal">{zh ? "· 所属仓库" : "· your warehouse"}</span>}
          </button>
        ))}
      </div>

      {data.canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="inv-stockin-toggle" size="sm" onClick={() => setShowStockIn((v) => !v)} className="bg-brand-500 hover:bg-brand-600 text-white">
            <PackagePlus className="h-4 w-4 mr-1" />{zh ? "入库登记" : "Stock in"}
          </Button>
          <Button data-testid="inv-history-toggle" size="sm" variant="outline" onClick={() => setShowHistory((v) => !v)}>
            <History className="h-4 w-4 mr-1" />{zh ? (showHistory ? "隐藏入库记录" : "入库记录") : "History"}
          </Button>
        </div>
      )}

      {data.canEdit && showStockIn && (
        <div data-testid="inv-stockin-form" className="bg-white rounded-xl border border-red-200 p-4 space-y-3">
          <p className="text-sm font-semibold text-slate-800">{zh ? `入库登记 · ${regionName(region)}` : `Stock In · ${regionName(region)}`}</p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <div className="md:col-span-2">
              <label className="text-[11px] text-slate-400">{zh ? "材料" : "Material"}</label>
              <select data-testid="inv-stockin-material" value={form.key} onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                className="w-full h-9 text-sm border border-slate-200 rounded-md px-2">
                <option value="">{zh ? "选择材料" : "Select"}</option>
                {data.rows.map((r) => <option key={r.key} value={r.key}>{r.name}（{zh ? "余" : "bal"} {r.balance}）</option>)}
              </select>
            </div>
            <div><label className="text-[11px] text-slate-400">{zh ? "入库数量" : "Qty"}</label><Input data-testid="inv-stockin-qty" type="number" min="0" value={form.qty} onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))} className="h-9 text-sm" /></div>
            <div><label className="text-[11px] text-slate-400">{zh ? "单价" : "Unit $"}</label><Input data-testid="inv-stockin-price" type="number" min="0" value={form.unitPrice} onChange={(e) => setForm((f) => ({ ...f, unitPrice: e.target.value }))} className="h-9 text-sm" /></div>
            <div><label className="text-[11px] text-slate-400">{zh ? "供应商" : "Supplier"}</label><Input data-testid="inv-stockin-supplier" value={form.supplier} onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))} className="h-9 text-sm" /></div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input data-testid="inv-stockin-note" placeholder={zh ? "备注（批次号等）" : "Note"} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className="h-9 text-sm flex-1" />
            <Button data-testid="inv-stockin-submit" size="sm" className="bg-brand-500 hover:bg-brand-600 text-white" onClick={submitStockIn}>{zh ? "确认入库" : "Submit"}</Button>
          </div>
        </div>
      )}

      {data.canEdit && showHistory && (
        <div data-testid="inv-history-panel" className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-100 text-sm font-semibold text-slate-700">{zh ? `入库记录 · ${regionName(region)}` : "Stock-in History"}</div>
          {history.length === 0 ? <p className="px-4 py-3 text-sm text-slate-400">{zh ? "暂无入库记录" : "No records"}</p> : (
            <ResponsiveTable className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
                <th className="text-left px-4 py-2">{zh ? "时间" : "Date"}</th><th className="text-left px-4 py-2">{zh ? "材料" : "Material"}</th>
                <th className="text-left px-4 py-2">{zh ? "供应商" : "Supplier"}</th><th className="text-right px-4 py-2">{zh ? "数量" : "Qty"}</th>
                <th className="text-right px-4 py-2">{zh ? "单价" : "Unit"}</th><th className="text-right px-4 py-2">{zh ? "金额" : "Cost"}</th><th className="text-left px-4 py-2">{zh ? "经办" : "By"}</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((r) => (
                  <tr key={r.id} data-testid={`inv-history-${r.id}`}>
                    <td className="px-4 py-2 text-slate-500">{fmtDT(r.createdAt)}</td>
                    <td className="px-4 py-2 text-slate-800">{r.name}</td>
                    <td className="px-4 py-2 text-slate-600">{r.supplier || "-"}</td>
                    <td className="px-4 py-2 text-right">{r.qty}</td>
                    <td className="px-4 py-2 text-right text-slate-500">${Number(r.unitPrice).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-medium">${Number(r.totalCost).toLocaleString()}</td>
                    <td className="px-4 py-2 text-slate-500">{r.createdBy}</td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <ResponsiveTable className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>
              <th className="text-left px-4 py-2.5">{zh ? "材料" : "Material"}</th>
              <th className="text-left px-4 py-2.5">{zh ? "单位" : "Unit"}</th>
              <th className="text-right px-4 py-2.5">{zh ? "当前余量" : "Balance"}</th>
              <th className="text-right px-4 py-2.5">{zh ? "最低阈值" : "Threshold"}</th>
              <th className="text-center px-4 py-2.5">{zh ? "状态" : "Status"}</th>
              {data.canEdit && <th className="text-center px-4 py-2.5">{zh ? "操作" : "Action"}</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.rows.map((row) => (
              <tr key={row.key} data-testid={`inv-row-${row.key}`} className={row.low ? "bg-red-50" : ""}>
                <td className="px-4 py-2 font-medium text-slate-800">{row.name}</td>
                <td className="px-4 py-2 text-slate-500">{row.unit}</td>
                <td className="px-4 py-2 text-right">
                  {data.canEdit ? (
                    <Input data-testid={`inv-balance-${row.key}`} type="number" defaultValue={row.balance}
                      className="h-11 sm:h-7 text-sm sm:text-xs w-full sm:w-24 text-right sm:ml-auto" onChange={(e) => setEdit(row.key, "balance", e.target.value)} />
                  ) : (
                    <span data-testid={`inv-balance-${row.key}`} className={`font-semibold ${row.low ? "text-red-600" : "text-slate-800"}`}>{row.balance}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  {data.canEdit ? (
                    <Input data-testid={`inv-threshold-${row.key}`} type="number" defaultValue={row.threshold}
                      className="h-11 sm:h-7 text-sm sm:text-xs w-full sm:w-20 text-right sm:ml-auto" onChange={(e) => setEdit(row.key, "threshold", e.target.value)} />
                  ) : (
                    <span className="text-slate-500">{row.threshold}</span>
                  )}
                </td>
                <td className="px-4 py-2 text-center">
                  {row.low
                    ? <span data-testid={`inv-status-${row.key}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full"><AlertTriangle className="h-3 w-3" />{zh ? "需补货" : "Low"}</span>
                    : <span data-testid={`inv-status-${row.key}`} className="text-[11px] font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">{zh ? "充足" : "OK"}</span>}
                </td>
                {data.canEdit && (
                  <td className="px-4 py-2 text-center">
                    <Button data-testid={`inv-save-${row.key}`} size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={!edits[row.key]} onClick={() => save(row)}>
                      <Save className="h-3.5 w-3.5 mr-1" />{zh ? "保存" : "Save"}
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      </div>
    </div>
  );
}
